import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, Plus, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BannedDomains = () => {
  const [loading, setLoading] = useState(true);
  const [bannedDomains, setBannedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);

  useEffect(() => {
    fetchBannedDomains();
  }, []);

  const fetchBannedDomains = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banned-domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBannedDomains(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch banned domains:", error);
      toast.error("Failed to fetch banned domains");
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    setAddingDomain(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/banned-domains`, 
        { domain: newDomain.trim().toLowerCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Domain added successfully");
      setNewDomain("");
      fetchBannedDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add domain");
    }
    setAddingDomain(false);
  };

  const handleToggleDomain = async (domainId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/banned-domains/${domainId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Domain status updated");
      fetchBannedDomains();
    } catch (error) {
      toast.error("Failed to update domain status");
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm("Are you sure you want to delete this domain?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/banned-domains/${domainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Domain deleted successfully");
      fetchBannedDomains();
    } catch (error) {
      toast.error("Failed to delete domain");
    }
  };

  const handleMarkAll = (checked) => {
    // Toggle all domains to the checked state
    bannedDomains.forEach(domain => {
      if (domain.is_active !== checked) {
        handleToggleDomain(domain.id, domain.is_active);
      }
    });
  };

  if (loading) {
    return <div style={{ color: '#B0B0B0' }}>Loading banned domains...</div>;
  }

  return (
    <div data-testid="banned-domains-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Banned Domains</h1>
        <p style={{ color: '#B0B0B0' }}>Manage email domains that are restricted from agency registration</p>
      </div>

      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)', backdropFilter: 'blur(12px)' }}>
        <CardHeader>
          <CardTitle className="flex items-center" style={{ color: '#FFD700' }}>
            <Shield className="mr-2 h-5 w-5" />
            Domain Restriction Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Add New Domain */}
          <form onSubmit={handleAddDomain} className="mb-6">
            <Label htmlFor="new-domain" style={{ color: '#E8E8E8', marginBottom: '8px', display: 'block' }}>
              Add New Domain
            </Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  id="new-domain"
                  placeholder="Enter domain (e.g., gmail.com)"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Enter domain name only, without @ symbol
                </p>
              </div>
              <Button
                type="submit"
                disabled={addingDomain}
                style={{ background: '#FFD700', color: '#0A1128', alignSelf: 'flex-start' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {addingDomain ? "Adding..." : "Add Domain"}
              </Button>
            </div>
          </form>

          {/* Mark All Checkbox */}
          <div className="mb-4 pb-3" style={{ borderBottom: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="mark-all"
                checked={bannedDomains.every(d => d.is_active)}
                onChange={(e) => handleMarkAll(e.target.checked)}
                className="h-4 w-4"
                style={{ accentColor: '#FFD700' }}
              />
              <Label htmlFor="mark-all" className="font-semibold cursor-pointer" style={{ color: '#FFD700' }}>
                Mark All as Restricted
              </Label>
            </div>
            <p className="text-xs mt-1 ml-6" style={{ color: '#B0B0B0' }}>
              Check/uncheck all domains at once
            </p>
          </div>

          {/* Domains List */}
          <div className="space-y-2 mb-4">
            {bannedDomains.length === 0 ? (
              <p style={{ color: '#B0B0B0', textAlign: 'center', padding: '20px' }}>
                No banned domains configured
              </p>
            ) : (
              bannedDomains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between p-3 rounded"
                  style={{
                    background: domain.is_active ? 'rgba(255, 215, 0, 0.1)' : 'rgba(176, 176, 176, 0.1)',
                    border: `1px solid ${domain.is_active ? 'rgba(255, 215, 0, 0.3)' : 'rgba(176, 176, 176, 0.2)'}`,
                  }}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="checkbox"
                      checked={domain.is_active}
                      onChange={() => handleToggleDomain(domain.id, domain.is_active)}
                      className="h-4 w-4"
                      style={{ accentColor: '#FFD700' }}
                    />
                    <div>
                      <p style={{ color: domain.is_active ? '#E8E8E8' : '#B0B0B0', fontWeight: '500' }}>
                        {domain.domain}
                      </p>
                      <p className="text-xs" style={{ color: '#B0B0B0' }}>
                        {domain.is_active ? '✓ Active - Blocked from registration' : '✗ Inactive - Allowed to register'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDomain(domain.id)}
                    style={{ color: '#FF4444' }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 rounded" style={{ background: 'rgba(74, 90, 138, 0.2)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <h4 className="font-semibold mb-2" style={{ color: '#FFD700' }}>
              <Shield className="inline h-4 w-4 mr-1" />
              How It Works
            </h4>
            <ul className="text-xs space-y-1" style={{ color: '#B0B0B0' }}>
              <li>• <strong style={{ color: '#E8E8E8' }}>Active (checked) domains:</strong> Users cannot register with these email domains</li>
              <li>• <strong style={{ color: '#E8E8E8' }}>Inactive (unchecked) domains:</strong> Users can register with these domains</li>
              <li>• <strong style={{ color: '#E8E8E8' }}>Default domains:</strong> Common public email providers (Gmail, Yahoo, Hotmail, etc.)</li>
              <li>• <strong style={{ color: '#E8E8E8' }}>Custom domains:</strong> Add any domain you want to restrict</li>
            </ul>
          </div>

          {/* Stats */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="p-3 rounded text-center" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <p className="text-2xl font-bold" style={{ color: '#FFD700' }}>
                {bannedDomains.filter(d => d.is_active).length}
              </p>
              <p className="text-xs" style={{ color: '#B0B0B0' }}>Active Bans</p>
            </div>
            <div className="p-3 rounded text-center" style={{ background: 'rgba(176, 176, 176, 0.1)', border: '1px solid rgba(176, 176, 176, 0.2)' }}>
              <p className="text-2xl font-bold" style={{ color: '#E8E8E8' }}>
                {bannedDomains.length}
              </p>
              <p className="text-xs" style={{ color: '#B0B0B0' }}>Total Domains</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BannedDomains;
